import { useCallback, useMemo, useState, useContext, useEffect } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    Grid2,
    Stack,
    Typography,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import Switch from '../../../Shared/Components/Switch';
import ListViewTableHeaderRow from '../../../Shared/Components/ListViewTable/ListViewTableHeaderRow';
import useGetInvoices from '../../hooks/useGetInvoices';
import useGetInvoicesIdList from '../../hooks/useGetInvoicesIdList';
import useLocalStorageState from '../../../utils/useLocalStorageState';
import { COLORS } from '../../../utils/Application_Constants';
import BookingContainerCollapse from './InvoiceInitiationTable/BookingContainerCollapse';
import {
    InvoicingSelectionContextProvider,
    useInvoicingSelectionContext,
} from '../../Contexts/InvoicingSelectionContext';
import useInvoicingFilters from '../../hooks/Invoicing/useFilters';
import FilterDrawer from '../../../Shared/Components/FilterDrawer/FilterDrawer';
import SearchInputWithDropdown from '../../../Shared/Components/SearchInputWithDropdown';
import {
    generateIdForExpenseWrapper,
    initiationSearchOptions,
    wrapExpenseItems,
} from '../../Configurations/invoicingConfig';
import { pluralize } from '../../../utils/helpers';
import { formatFiltersForApi } from '../../../utils/filterUtils';
import InvoiceInitiationConfirmationModal from './InvoiceInitiationTable/InvoiceInitiationConfirmationModal';
import PaginationBar from '../../../Shared/Components/PaginationBar';
import { AdminAuthContext } from '../../Contexts/AdminAuthContext';
import { useToast } from '../../Contexts/ToastContext';

const tableHeaders = [
    { label: 'Client', key: 'client', size: 1, sortable: false },
    { label: 'Clinician', key: 'clinician', size: 1, sortable: false },
    { label: 'Bookings', key: 'booking', size: 1, sortable: false },
    { label: '# of Location', key: 'numberOfLocation', size: 1, sortable: false },
    { label: 'Frequency', key: 'frequency', size: 1, sortable: false },
    { label: 'Collector', key: 'collector', size: 1, sortable: false },
    { label: 'Types', key: 'types', size: 1, sortable: false },
];

const pageSize = 20;

const InitiationTable = ({ pageType = 'invoiceInitiation' }) => {
    const [disableSelection, setDisableSelection] = useState(true);
    const {
        selectStatus,
        handleSelectAllBookings,
        handleToggleSelectBookingChunk,
        selectedItemsCount,
        handleInitializeNewBookings,
        handleReset: resetSelection,
    } = useInvoicingSelectionContext();
    const { location, replace, push } = useHistory();
    const [doNotSendToClient, setDoNotSendToClient] = useState(false);
    const [successState, setSuccessState] = useState({ showAlert: false, count: 0, action: '' });
    const [showDisabledAlert, setShowDisabledAlert] = useState(false);
    const { showSuccessToast } = useToast();

    const { state: sortBy, setValue: setSortBy } = useLocalStorageState(`${pageType}SortField`);
    const { state: sortDirection, setValue: setSortDirection } = useLocalStorageState(
        `${pageType}SortDirection`,
        'asc'
    );
    const { state: searchData, setValue: setSearch } = useLocalStorageState(`${pageType}Search`, {
        searchWord: '',
        searchColumns: '',
    });
    const { state: innerSearchData, setValue: setInnerSearch } = useLocalStorageState(
        `${pageType}InnerSearch`,
        { InnerSearchWord: '', InnerSearchColumns: '' }
    );

    const { isInvoicingSystemAdminUser } = useContext(AdminAuthContext);

    const {
        filters,
        handleApplyFilter,
        filtersSetup,
        isLoadingFilterConfig,
        appliedFiltersDescription,
    } = useInvoicingFilters(pageType);

    const [page, setPage] = useState(() => {
        const urlParams = new URLSearchParams(location.search);
        if (urlParams.has('page') && parseInt(urlParams.get('page'), 10) > 0) {
            return parseInt(urlParams.get('page'), 10);
        }
        replace(`${location.pathname}?page=1`);
        return 1;
    });
    const handlePageChange = (_, newPage) => {
        push(`${location.pathname}?page=${newPage}`);
        setPage(newPage);
    };

    const requestParams = useMemo(
        () => ({
            offset: (page - 1) * pageSize,
            pageSize,
            ...formatFiltersForApi(filters, filtersSetup),
            ...(sortBy ? { orderBy: sortBy, dir: sortDirection } : {}),
            ...(searchData?.searchWord ? searchData : {}),
            ...(innerSearchData?.InnerSearchWord ? innerSearchData : {}),
        }),
        [page, sortBy, sortDirection, filters, filtersSetup, searchData, innerSearchData, pageSize]
    );

    const {
        data: bookings,
        isLoading,
        refetch: refetchGetInvoices,
    } = useGetInvoices(requestParams);

    const { isFetching: isFetchingAllSelectableIds, refetch: fetchAllSelectableIds } =
        useGetInvoicesIdList(
            {
                ...formatFiltersForApi(filters, filtersSetup),
                ...(searchData?.searchWord ? searchData : {}),
            },
            {
                enabled: false,
                staleTime: Infinity,
                onSuccess: (allItemsIds) => {
                    const selectableBookings = allItemsIds?.data?.map((booking) => ({
                        bookingId: booking.bookingId,
                        clientId: booking.clientId,
                        timesheets: booking.processedTimesheets || [],
                        expenses:
                            booking?.expenses?.length > 0
                                ? [
                                      {
                                          id: generateIdForExpenseWrapper(booking.expenses), // generate id for expense items wrapped based on expenses items ids
                                          items: booking.expenses.map((expenseId) => ({
                                              id: expenseId,
                                          })),
                                      },
                                  ]
                                : [],
                    }));

                    handleInitializeNewBookings(selectableBookings || []);
                    setDisableSelection(false);
                },
                onError: () => {
                    setDisableSelection(true);
                    setShowDisabledAlert(true);
                },
            }
        );

    const fetchAllSelectableIdsAndResetSelection = useCallback(() => {
        setShowDisabledAlert(false);
        resetSelection();
        fetchAllSelectableIds();
    }, []);

    const {
        data: allItemsIdsWithinInnerSearch,
        isFetching: isFetchingAllSelectableIdsWithinInnerSearch,
        refetch: fetchAllSelectableIdsWithinInnerSearch,
    } = useGetInvoicesIdList(
        {
            ...formatFiltersForApi(filters, filtersSetup),
            ...(searchData?.searchWord ? searchData : {}),
            ...(innerSearchData?.InnerSearchWord ? innerSearchData : {}),
        },
        {
            enabled: false,
            onSuccess: () => setDisableSelection(false),
            onError: () => setDisableSelection(true),
        }
    );

    const handleRefetch = () => {
        refetchGetInvoices();
        fetchAllSelectableIdsAndResetSelection();
    };

    useEffect(() => {
        if (successState.showAlert) {
            showSuccessToast('Your invoice(s) have been queued for creation.');
            setSuccessState({ showAlert: false, count: 0, action: '' });
        }
    }, [successState]);

    useEffect(() => {
        if (page !== 1) handlePageChange(null, 1);
        setDisableSelection(true);
        fetchAllSelectableIdsAndResetSelection();
    }, [filters, searchData]); // inner search data should not be included here as it does not affect global selection/results

    useEffect(() => {
        // don't fetch if there is no inner search word, otherwise the query key will match query key for fetchAllSelectableIds (will clean selection on innerSearch clear)
        if (!innerSearchData?.InnerSearchWord) return;
        setDisableSelection(true);
        fetchAllSelectableIdsWithinInnerSearch();
    }, [filters, searchData, innerSearchData]);

    const [showInvoiceInitiationConfirmationModal, setShowInvoiceInitiationConfirmationModal] =
        useState(false);
    const [invoiceAction, setInvoiceAction] = useState('invoice'); // 'invoice' or 'pre-invoice'
    const totalCount = bookings?.totalCount || 0;

    const handlePreInvoiceClick = () => {
        setInvoiceAction('pre-invoice');
        setShowInvoiceInitiationConfirmationModal(true);
    };

    const handleInvoiceClick = () => {
        setInvoiceAction('invoice');
        setShowInvoiceInitiationConfirmationModal(true);
    };

    const handleSortChange = useCallback((key, direction) => {
        setSortBy(key);
        setSortDirection(direction);
    }, []);

    const renderPagination = (position) => {
        if (isLoading || !totalCount) return null;
        return (
            <Box
                sx={{ mt: position === 'top' ? 0 : '15px', mb: position === 'bottom' ? 0 : '15px' }}
            >
                <PaginationBar
                    page={page}
                    totalPages={Math.ceil(totalCount / pageSize)}
                    handlePageChange={handlePageChange}
                />
            </Box>
        );
    };

    const bookingItems = useMemo(() => bookings?.data?.map(wrapExpenseItems) || [], [bookings]);

    const handleSelectAll = useCallback(() => {
        if (isFetchingAllSelectableIdsWithinInnerSearch || isFetchingAllSelectableIds) return;
        if (innerSearchData?.InnerSearchWord && allItemsIdsWithinInnerSearch?.data?.length > 0) {
            const innerSearchBookingIds =
                allItemsIdsWithinInnerSearch?.data?.map((item) => item.bookingId) || [];
            handleToggleSelectBookingChunk(innerSearchBookingIds);
        } else {
            handleSelectAllBookings();
        }
    }, [
        isFetchingAllSelectableIds,
        isFetchingAllSelectableIdsWithinInnerSearch,
        innerSearchData?.InnerSearchWord,
        allItemsIdsWithinInnerSearch?.data,
    ]);

    return (
        <>
            <Grid2 container alignItems="center" spacing={1} mt="12px">
                <FilterDrawer
                    isLoading={isLoadingFilterConfig || isLoading}
                    config={filtersSetup}
                    onChange={handleApplyFilter}
                    storedValues={filters}
                />
                <SearchInputWithDropdown
                    isLoading={isLoading}
                    options={initiationSearchOptions}
                    initialSearch={searchData?.searchWord}
                    initialSearchKey={searchData?.searchColumns}
                    onSubmit={({ search, searchKey }) =>
                        setSearch({ searchWord: search, searchColumns: searchKey })
                    }
                    sx={{ ml: 'auto' }}
                />
            </Grid2>

            <Typography sx={{ my: 2, fontSize: 12, fontWeight: '400', color: COLORS.LT_MIDNIGHT }}>
                {`${totalCount} ${pluralize(totalCount, 'item')}`}
                {appliedFiltersDescription}
            </Typography>

            {renderPagination('top')}

            {showDisabledAlert && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setShowDisabledAlert(false)}>
                    Selection has been disabled due to an error fetching selectable items. Please
                    refresh the page or adjust your filters.
                </Alert>
            )}

            <Box
                sx={{
                    py: '22px',
                    px: '26px',
                    backgroundColor: COLORS.WHITE,
                    borderRadius: '10px',
                }}
            >
                <Typography>Unbill items</Typography>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: '30px', mb: '20px' }}
                >
                    <SearchInputWithDropdown
                        isLoading={isLoading}
                        options={initiationSearchOptions}
                        placeholder="Search within results"
                        initialSearch={innerSearchData?.InnerSearchWord}
                        initialSearchKey={innerSearchData?.InnerSearchColumns}
                        onSubmit={({ search, searchKey }) =>
                            setInnerSearch({
                                InnerSearchWord: search,
                                InnerSearchColumns: searchKey,
                            })
                        }
                    />
                    {isInvoicingSystemAdminUser && (
                        <>
                            <FormControlLabel
                                sx={{
                                    ml: 'auto',
                                    '& .MuiFormControlLabel-label': {
                                        color: COLORS.LT_MIDNIGHT,
                                        fontSize: 14,
                                        fontStyle: 'normal',
                                        fontWeight: 400,
                                        lineHeight: '16.5px',
                                    },
                                }}
                                checked={doNotSendToClient}
                                onChange={(e) => setDoNotSendToClient(e.target.checked)}
                                control={<Switch />}
                                label="Do not sent to client"
                            />
                            <Stack direction="row" alignItems="center" gap="10px">
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    disabled={selectedItemsCount === 0}
                                    onClick={handlePreInvoiceClick}
                                >
                                    Pre-invoice ({selectedItemsCount})
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    disabled={selectedItemsCount === 0}
                                    onClick={handleInvoiceClick}
                                >
                                    Invoice Selected ({selectedItemsCount})
                                </Button>
                            </Stack>
                        </>
                    )}
                </Stack>
                {bookingItems.length > 0 ? (
                    <>
                        <ListViewTableHeaderRow
                            selectStatus={!disableSelection ? selectStatus : undefined}
                            onSelectAll={handleSelectAll}
                            selectColumnProps={{
                                sx: {
                                    justifyContent: 'flex-end',
                                    width: '90px',
                                    pr: '2.5px !important',
                                },
                            }}
                            columns={tableHeaders}
                            onSortChange={handleSortChange}
                            sortBy={sortBy}
                            sortDirection={sortDirection}
                            shouldCleanSort
                            sortIconPosition="left"
                        />

                        {bookingItems.map((booking) => (
                            <BookingContainerCollapse
                                key={booking.bookingId}
                                booking={booking}
                                disableSelection={disableSelection}
                            />
                        ))}
                    </>
                ) : (
                    <div>{isLoading ? <CircularProgress /> : 'No invoices found'}</div>
                )}
            </Box>
            <InvoiceInitiationConfirmationModal
                open={showInvoiceInitiationConfirmationModal}
                onClose={() => setShowInvoiceInitiationConfirmationModal(false)}
                action={invoiceAction}
                doNotSendToClient={doNotSendToClient}
                setDoNotSendToClient={setDoNotSendToClient}
                setSuccessState={setSuccessState}
                refetch={handleRefetch}
            />

            {renderPagination('bottom')}
        </>
    );
};

export default function InitiationTableWithSelectionContext() {
    return (
        <InvoicingSelectionContextProvider bookings={[]} totalCount={[]}>
            <InitiationTable />
        </InvoicingSelectionContextProvider>
    );
}
